import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useLazyQuery } from '@apollo/client';

import { Box, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames/bind';

import CustomTextField from '../../../../../components/Textfield/CustomTextField';
import colors from '../../../../../../constants/colors';
import CustomButtons from '../../../../../components/Button/CustomButtons';
import ACDList from './ACDList';
import * as Q from '../../../../../../constants/graphqlQueries';

const useStyles = makeStyles(() => ({
    rowFooter: {
        display: 'flex',
        justifyContent: 'space-evenly',
        padding: '0 20px',
    },
    container: {
        display: 'flex',
        marginTop: '5px',
        '@media only screen and (max-width: 1200px)': {
            flexDirection: 'column'
        }
    },
    boxLayoutTitle: {
        fontWeight: '700',
        lineHeight: '20px',
        color: colors.blue,
        marginBottom: '12px',
        fontSize: '1.8rem',
    },
    boxDivLayout: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        margin: '0 3rem 0 0'
    },
    footerButtonContainer: {
        marginBottom: '16px',
    },
    boxContainerLayout: {
        display: 'flex',
        marginLeft: '1rem'
    },
    skeleton: {
        transform: 'none'
    }
}));
const sortItems = (a, b) => {
    const nameA = a.MappedCode.toUpperCase();
    const nameB = b.MappedCode.toUpperCase();
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    return 0;
};
const FieldIndex = { codeLabel: 1, codeDescription: 2, availableCodes: 3 };
const getPlaceholder = value => {
    const placeholders = {
        1: 'Loading...',
        2: 'Search for the ACD codes you would like to see',
        3: 'Data not found'
    }
    return value in placeholders ? placeholders[value] : 'Choose a code';
};
const ACD = 'ACD';

const ACDCodeMapping = ({ ACDCodeMappingId, handleClearEdit, handleSubmit, error }) => {
    const classes = useStyles();
    const cx = classNames.bind(classes);

    const [isSaveDisabled, setIsSaveDisabled] = useState(!ACDCodeMappingId);
    const [inputFieldDisabled, setInputFieldDisabled] = useState(0);
    const [codeLabel, setCodeLabel] = useState('');
    const [codeDescription, setCodeDescription] = useState('');

    const [availableCodes, setAvailableCodes] = useState([]);
    const [selectedAvailableCodes, setSelectedAvailableCodes] = useState([]);
    const [filteredSelectedAvailableCodes, setFilteredSelectedAvailableCodes] = useState([]);

    const [filterAvailableCodes, setFilterAvailableCodes] = useState('');
    const [filterSelectedAvailableCodes, setFilterSelectedAvailableCodes] = useState('');
    const [placeholder, setPlaceholder] = useState('');

    const [loadingSubmit, setLoadingSubmit] = useState(false);

    const [getACDState, { data: ACDStates, loading: loadACDStates }] = useLazyQuery(Q.ALL_ACD_STATES);
    const [getCodeMappings, { data: ACDCodeMappingData, loading: isLoadingCodeMappings }] = useLazyQuery(
        Q.CODE_MAPPINGS_QUERY,
    );

    const handleChangeCodeLabel = value => setCodeLabel(value);
    const handleChangeCodeDescription = value => setCodeDescription(value);
    const handleSelectedCode = newValueSelected => {
        const newValue = newValueSelected;
        const searchAvailableCodes = [...selectedAvailableCodes, newValue].sort((a, b) => sortItems(a, b));
        setSelectedAvailableCodes(searchAvailableCodes);
        setFilteredSelectedAvailableCodes(searchAvailableCodes);
        if (availableCodes.length) setAvailableCodes(availableCodes.filter(code => code.mappingKey !== newValueSelected.mappingKey));
    };
    const handleUnselectedCode = valueSelected => {
        const newValue = valueSelected;
        const avaliblesCodesFiltered = selectedAvailableCodes.filter(code => code.mappingKey !== valueSelected.mappingKey);
        setSelectedAvailableCodes(avaliblesCodesFiltered);
        setFilteredSelectedAvailableCodes(avaliblesCodesFiltered);
        const searchAvailableCodes = [...availableCodes, newValue].sort((a, b) => sortItems(a, b))
        setAvailableCodes(searchAvailableCodes);
    };
    const handleSearchAvailableCodes = value => {
        if (!value) setAvailableCodes([]);
        setFilterAvailableCodes(value);
    };
    const handleChangeFilterSelectedAvailableCodes = value => setFilterSelectedAvailableCodes(value);
    const handleCancel = () => {
        setCodeLabel('');
        setCodeDescription('');
        setFilterAvailableCodes('');
        setFilterSelectedAvailableCodes('');
        setAvailableCodes([]);
        setSelectedAvailableCodes([]);
        handleClearEdit();
    };
    const handleSave = () => {
        setLoadingSubmit(true);
        const input = {
            ACDCodeMappingId,
            codeLabel,
            codeDescription,
            ACDCodes: selectedAvailableCodes.map(({ mappingKey }) => ({ mappingKey }) ),
        }
        handleSubmit(input)
    };
    const isInputFieldDisabled = inputIndex => {
        if (inputFieldDisabled && inputIndex > inputFieldDisabled) return true;
        return false;
    };
    useEffect(() => {
        if (ACDCodeMappingId) getCodeMappings({
            variables: {
                input: { codeMappingType: ACD, codeMappingId: ACDCodeMappingId }
            },
        });
    }, [ACDCodeMappingId]);
    useEffect(() => {
        if (ACDCodeMappingData) {
            ACDCodeMappingData.codeMappingsScheduleACD.forEach(element => {
                setCodeLabel(element.Label);
                setCodeDescription(element.Description);
                setSelectedAvailableCodes(element.avaliblesCodes);
                setFilteredSelectedAvailableCodes(element.avaliblesCodes);
            });
        };
    }, [ACDCodeMappingData]);
    useEffect(() => {
        if (ACDStates && filterAvailableCodes) {
            let filteredACDStates = ACDStates.getAllACDState;
            if (selectedAvailableCodes.length) {
                filteredACDStates = ACDStates.getAllACDState.filter(st =>
                    !selectedAvailableCodes.find(ac =>
                        st.mappingKey === ac.mappingKey)
                );
            }
            setAvailableCodes(filteredACDStates);
        }
    }, [ACDStates]);
    useEffect(() => {
        if (filterAvailableCodes.length) getACDState({ variables: { stateLabel: filterAvailableCodes } });
    }, [filterAvailableCodes]);
    useEffect(() => {
        if (filterSelectedAvailableCodes) {
            setFilteredSelectedAvailableCodes(selectedAvailableCodes.filter((ac) => ac.MappedCode.toUpperCase().includes(filterSelectedAvailableCodes.toUpperCase())));
        } else {
            setFilteredSelectedAvailableCodes(selectedAvailableCodes);
        }
    }, [filterSelectedAvailableCodes]);
    useEffect(() => {
        // State watcher
        if (!codeLabel) setInputFieldDisabled(FieldIndex.codeLabel);
        else if (!codeDescription) setInputFieldDisabled(FieldIndex.codeDescription);
        else if (!selectedAvailableCodes.length) setInputFieldDisabled(FieldIndex.availableCodes);
        else setInputFieldDisabled(null);
    }, [codeLabel, codeDescription, availableCodes, selectedAvailableCodes, selectedAvailableCodes])
    useEffect(() => {
        if (inputFieldDisabled === null) setIsSaveDisabled(false);
        else setIsSaveDisabled(true);
    }, [inputFieldDisabled]);
    useEffect(() => {
        if(loadACDStates) setPlaceholder(getPlaceholder(1));
        else setPlaceholder(getPlaceholder(2));
        if (!availableCodes.length && filterAvailableCodes && !loadACDStates) setPlaceholder(getPlaceholder(3));
    }, [availableCodes, loadACDStates]);
    useEffect(() => {
        if(error) setLoadingSubmit(false);
    }, [error]);

    return (
        <>
            <Grid className={classes.boxContainerLayout}>
                <Grid item className={classes.boxDivLayout}>
                    {isLoadingCodeMappings ? <Skeleton width={250} height={40} />
                    : <p className={classes.boxLayoutTitle}>Dimensions</p>}
                    <Grid container direction='column' className={classes.container}>
                        <CustomTextField
                            title='Code Label'
                            value={codeLabel}
                            handleChange={handleChangeCodeLabel}
                            placeholder='Input text'
                            mandatory
                            loading={isLoadingCodeMappings}
                            maxLength={25}
                        />
                        <CustomTextField
                            title='Code Description'
                            value={codeDescription}
                            handleChange={handleChangeCodeDescription}
                            placeholder='Input text'
                            disabled={isInputFieldDisabled(FieldIndex.codeDescription)}
                            mandatory
                            loading={isLoadingCodeMappings}
                            maxLength={100}
                        />
                    </Grid>
                </Grid>
                <Grid item className={classes.boxDivLayout} xs={8}>
                    {isLoadingCodeMappings ? <Skeleton width={250} height={40} />
                    : <p className={classes.boxLayoutTitle}>Code Selection</p>}
                    <Grid className={classes.container}>
                        <Grid item className={classes.boxDivLayout} xs={6}>
                            <CustomTextField
                                title='Available Codes'
                                value={filterAvailableCodes}
                                handleChange={handleSearchAvailableCodes}
                                placeholder='Search'
                                disabled={isInputFieldDisabled(FieldIndex.availableCodes)}
                                inputIcon
                                mandatory
                                loading={isLoadingCodeMappings}
                            />
                            {
                                isLoadingCodeMappings ? <Skeleton className={classes.skeleton} width={400} height={200} /> 
                                : <Box className='selection__box__ACD'>
                                    <ACDList itemList={availableCodes} handleItemList={handleSelectedCode} placeholder={placeholder} />
                                </Box>
                            }
                        </Grid>
                        <Grid item className={classes.boxDivLayout} xs={6}>
                            <CustomTextField
                                title='Selected Codes'
                                value={filterSelectedAvailableCodes}
                                handleChange={handleChangeFilterSelectedAvailableCodes}
                                placeholder='Search'
                                inputIcon
                                loading={isLoadingCodeMappings}
                            />
                            {
                                isLoadingCodeMappings ? <Skeleton className={classes.skeleton} width={400} height={200} />
                                : <Box className='selection__box__ACD'>
                                    <ACDList itemList={filteredSelectedAvailableCodes} handleItemList={handleUnselectedCode} itemIconList placeholder='Choose a code' />
                                </Box>
                            }
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item className={cx(classes.boxDivLayout, classes.footerButtonContainer)} xs={12}>
                <Grid container direction='row-reverse'>
                    <Grid item xs={4}>
                        <Box className={classes.rowFooter}>
                            <CustomButtons
                                handleClick={handleCancel}
                                name='Cancel'
                                loading={isLoadingCodeMappings}
                            />
                            <CustomButtons
                                handleClick={handleSave}
                                name='Save'
                                disabled={isSaveDisabled}
                                tooltipFree
                                loadingButton={loadingSubmit}
                                loading={isLoadingCodeMappings}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

ACDCodeMapping.propTypes = {
    ACDCodeMappingId: PropTypes.number,
    handleClearEdit: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    error: PropTypes.bool.isRequired
};

ACDCodeMapping.defaultProps = {
    ACDCodeMappingId: undefined
};
export default ACDCodeMapping;